import { useTranslation } from 'react-i18next';
import type { Location, Screen } from '@ghostscreen/contracts';
import { DisplayCard } from './DisplayCard';
import { LocationActionsMenu } from './LocationActionsMenu';

type DisplayGroupProps = {
  location: Location;
  screens: Screen[];
  onlineCount: number;
  onManageScreen: (screenId: string) => void;
  onPlayContent: (screenId: string) => void;
  onAddDisplay: (locationId: string) => void;
  onEditLocation: (location: Location) => void;
  onDuplicateLocation: (location: Location) => void;
  onDeleteLocation: (location: Location) => void;
  duplicatingLocationId?: string | null;
};

export function DisplayGroup({
  location,
  screens,
  onlineCount,
  onManageScreen,
  onPlayContent,
  onAddDisplay,
  onEditLocation,
  onDuplicateLocation,
  onDeleteLocation,
  duplicatingLocationId = null,
}: DisplayGroupProps) {
  const { t } = useTranslation('displays');
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-line border-s-[3px] border-s-signal bg-panel px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[15px] font-bold text-ink">{location.name}</h2>
          <p className="m-0 mt-0.5 text-[13px] text-muted">
            {t('counts.displays', { count: screens.length })}
            {screens.length > 0 ? ` · ${t('counts.online', { count: onlineCount })}` : ''}
            {location.timezone ? ` · ${location.timezone}` : ''}
          </p>
        </div>
        <LocationActionsMenu
          locationName={location.name}
          duplicating={duplicatingLocationId === location.id}
          onEdit={() => onEditLocation(location)}
          onDuplicate={() => onDuplicateLocation(location)}
          onDelete={() => onDeleteLocation(location)}
        />
        <button
          type="button"
          onClick={() => onAddDisplay(location.id)}
          className="shrink-0 rounded-lg px-3.5 py-2 text-sm font-semibold text-[var(--gs-accent-ink)]"
          style={{ background: 'var(--gs-accent-solid)' }}
        >
          {t('group.addDisplay')}
        </button>
      </div>
      {screens.length === 0 ? (
        <p className="m-0 text-sm text-muted">{t('group.empty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {screens.map((screen) => (
            <DisplayCard
              key={screen.id}
              screen={screen}
              locationName={location.name}
              onManage={() => onManageScreen(screen.id)}
              onPlayContent={() => onPlayContent(screen.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
